import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Grid, Icon, Skeleton } from "@mui/material";

import MDTypography from "ui/MDTypography";
import MDBox from "ui/MDBox";
import MDAlert from "ui/MDAlert";
import { DetailPageLayout } from "examples/LayoutContainers/DetailPageLayout";

import fundsServices from "services/funds-services";
import karkunService from "services/karkun-service";
import authService from "services/auth-service";

const FundsDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [updatedByNames, setUpdatedByNames] = useState({});
  const [funds, setFunds] = useState(null);
  const [karkunNames, setKarkunNames] = useState({});
  const [addedByNames, setAddedByNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(false);

  // -------------------------------
  // Fetch FUND DETAILS
  // -------------------------------
  useEffect(() => {
    const fetchData = async () => {
      try {
        const resp = await fundsServices.get(id);
        if (resp.success) setFunds(resp.fund);
      } catch {
        setNotification({
          type: "error",
          message: "Failed to load fund details",
        });
      }
      setLoading(false);
    };
    fetchData();
  }, [id]);

  // -------------------------------
  // Fetch Names (SMOOTH)
  // -------------------------------
  useEffect(() => {
    if (!funds) return;

    const loadNames = async () => {
      const kn = {};
      const ab = {};
      const ub = {};

      for (const f of funds) {
        // --- Karkun Name ---
        try {
          const r1 = await karkunService.getById(f.karkun);
          kn[f.karkun] = r1.success ? r1.karkun.name : "Unknown";
        } catch {
          kn[f.karkun] = "Unknown";
        }

        // --- Added By Name ---
        try {
          const r2 = await authService.getUserById(f.addedBy);
          ab[f.addedBy] = r2.success ? r2.user.name : "Unknown";
        } catch {
          ab[f.addedBy] = "Unknown";
        }

        // --- Updated By Name (ONLY IF EXISTS) ---
        if (f.updatedBy) {
          try {
            const r3 = await authService.getUserById(f.updatedBy);
            ub[f.updatedBy] = r3.success ? r3.user.name : "Unknown";
          } catch {
            ub[f.updatedBy] = "Unknown";
          }
        }
      }

      setKarkunNames(kn);
      setAddedByNames(ab);
      setUpdatedByNames(ub);
    };


    loadNames();
  }, [funds]);

  // -------------------------------
  // Total Amount
  // -------------------------------
  const totalAmount =
    funds?.reduce((sum, f) => sum + Number(f.amount), 0) || 0;

  // -------------------------------
  // SHIMMER SKELETON HELPER
  // -------------------------------
  const shimmer = (w = 120) => (
    <Skeleton width={w} animation="wave" />
  );

  // -------------------------------
  // Fund Cards
  // -------------------------------
  const renderCards = () => {
    if (!funds?.length)
      return (
        <MDTypography variant="body2" color="error" mt={2}>
          No funds found
        </MDTypography>
      );

    return (
      <Grid container spacing={3} mt={1}>
        {funds.map((f) => (
          <Grid item xs={12} sm={6} md={4} key={f.id}>
            <MDBox
              p={3}
              borderRadius="xl"
              sx={{
                background: "rgba(255,255,255,0.75)",
                backdropFilter: "blur(14px)",
                border: "1px solid rgba(220,220,220,0.4)",
                boxShadow:
                  "0 4px 20px rgba(0,0,0,0.05), inset 0 1px 2px rgba(255,255,255,0.4)",
                position: "relative",
                transition: "all .3s ease",
                "&:hover": {
                  transform: "translateY(-6px)",
                  boxShadow:
                    "0 8px 30px rgba(0,0,0,0.1), inset 0 1px 2px rgba(255,255,255,0.5)",
                },
              }}
            >
              <MDBox
                onClick={() => navigate(`/funds/${f.id}/edit`)}
                sx={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  cursor: "pointer",
                  width: 32,
                  height: 32,
                  borderRadius: "10px",
                  border: "1.5px solid rgba(255,255,255,0.8)",   // ALWAYS visible
                  backdropFilter: "blur(6px)",
                  background: "rgba(255,255,255,0.15)",         // Glass badge
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.12)",
                  transition: "0.2s ease",
                  "&:hover": {
                    transform: "scale(1.07)",
                    background: "rgba(255,255,255,0.25)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
                  },
                }}
              >
                <Icon sx={{ fontSize: 18, color: "#111" }}>edit</Icon>
              </MDBox>


              {/* Header */}
              <MDTypography
                variant="h6"
                fontWeight="bold"
                sx={{
                  mb: 2,
                  background: "linear-gradient(90deg,#0f172a,#334155)",
                  color: "transparent",
                  WebkitBackgroundClip: "text",
                }}
              >
                {karkunNames[f.karkun] || shimmer(140)}
              </MDTypography>

              {/* Divider */}
              <MDBox
                sx={{
                  height: "1px",
                  width: "100%",
                  background: "linear-gradient(90deg,#e5e7eb,transparent)",
                  mb: 2,
                }}
              />

              {[
                ["Fund Type", f.fundtype],
                ["Amount", `Rs. ${Number(f.amount).toLocaleString()}`],
                ["Added By", addedByNames[f.addedBy] || shimmer(100)],
                ["Date", new Date(f.createdAt).toLocaleDateString()],
                ["Time", new Date(f.createdAt).toLocaleTimeString()],

                ...(f.updatedBy
                  ? [
                    ["Updated By", updatedByNames[f.updatedBy] || shimmer(100)],
                    ["Updated At", new Date(f.updatedAt).toLocaleString()],
                  ]
                  : [])
              ].map(([label, value], i) => (
                <MDBox
                  key={i}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  py={0.7}
                  sx={{
                    "&:not(:last-child)": {
                      borderBottom: "1px dashed #e5e7eb",
                    },
                  }}
                >
                  <MDTypography
                    variant="button"
                    sx={{ color: "#6b7280", fontSize: "0.88rem" }}
                  >
                    {label}
                  </MDTypography>

                  <MDTypography
                    variant="button"
                    fontWeight="bold"
                    sx={{ fontSize: "0.9rem" }}
                  >
                    {value}
                  </MDTypography>
                </MDBox>
              ))}
            </MDBox>

          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <DetailPageLayout
      title="Funds Details"
      loading={loading}
      backButtonRoute="/funds"
      renderContent={() => (
        <>
          {notification && (
            <MDBox mb={2}>
              <MDAlert color={notification.type}>
                <MDTypography color="white">
                  {notification.message}
                </MDTypography>
              </MDAlert>
            </MDBox>
          )}

          <MDBox display="flex" justifyContent="center" width="100%">
            <MDBox
              p={2}
              borderRadius="lg"
              sx={{
                bgcolor: "#ffffff",
                border: "1px solid #e2e5ea",
                boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
                width: "100%",
                maxWidth: "420px",   // ⭐ FIXED + PREMIUM WIDTH
                minHeight: "70px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <MDBox>
                <MDTypography
                  variant="caption"
                  color="text"
                  sx={{ fontSize: "12px", fontWeight: 500 }}
                >
                  Total Collection
                </MDTypography>

                <MDTypography
                  variant="h6"
                  sx={{
                    marginTop: "2px",
                    fontWeight: 700,
                    fontSize: "20px",
                    color: "#1f2937",
                  }}
                >
                  Rs. {totalAmount.toLocaleString()}
                </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>

          {renderCards()}
        </>
      )}
    />
  );
};

export default FundsDetail;